import { getCurrentUser } from '../store.js';
import {
  apiGetBoardFeed,
  apiMarkBoardRead,
  apiDeleteBoardPost,
  apiDownloadBoardPostCalendarIcs,
  apiDownloadBoardAttachmentByUrl,
  apiGetBoardPostReadStatus,
} from '../api.js';
import { renderBoardComposer } from './BoardComposer.js';
import { renderBoardModerationView } from './BoardModerationView.js';

function isModerationRole(role) {
  return /^(admin|director)$/.test(String(role || '').trim());
}

function escapeHtml(text) {
  return String(text || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeAttr(text) {
  return escapeHtml(text).replaceAll('"', '&quot;');
}

function displayDoctorLabel(item) {
  const sigla = String(item?.sigla || '').trim();
  const nome = String(item?.nome || '').trim();
  const cognome = String(item?.cognome || '').trim();
  const parts = [sigla, nome, cognome].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return String(item?.username || item?.userId || item?.id || 'Destinatario');
}

function openReadStatusModal(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" style="max-width:860px;">
      <div class="modal-header">
        <strong>Stato letture destinatari</strong>
      </div>
      <div class="modal-body">
        <div style="color:var(--text-secondary); font-size:.9rem; margin-bottom:.7rem;">
          ${escapeHtml(payload?.title || 'Comunicazione')}<br>
          Destinatari: ${Number(payload?.totalRecipients || 0)} · Letto: ${Number(payload?.readCount || 0)} · Confermato: ${Number(payload?.ackCount || 0)}
        </div>
        <div style="max-height:52vh; overflow:auto;">
          <table style="width:100%; border-collapse:collapse;">
            <thead>
              <tr>
                <th style="text-align:left; padding:.4rem; border-bottom:1px solid var(--border);">Medico destinatario</th>
                <th style="text-align:left; padding:.4rem; border-bottom:1px solid var(--border);">Lettura</th>
                <th style="text-align:left; padding:.4rem; border-bottom:1px solid var(--border);">Conferma</th>
              </tr>
            </thead>
            <tbody>
              ${items.length ? items.map((r) => `
                <tr>
                  <td style="padding:.45rem; border-bottom:1px solid var(--border);">${escapeHtml(displayDoctorLabel(r))}</td>
                  <td style="padding:.45rem; border-bottom:1px solid var(--border);">${escapeHtml(r?.readAt || 'Non letta')}</td>
                  <td style="padding:.45rem; border-bottom:1px solid var(--border);">${r?.acknowledged ? 'Confermata' : 'Non confermata'}</td>
                </tr>
              `).join('') : '<tr><td colspan="3" style="padding:.6rem; color:var(--text-secondary);">Nessun destinatario risolto.</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-close>Chiudi</button>
      </div>
    </div>
  `;
  const close = () => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('[data-close]')?.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  document.body.appendChild(overlay);
}

function attachmentTypeSymbol(att) {
  const fileName = String(att?.fileName || '').toLowerCase();
  const mime = String(att?.mimeType || '').toLowerCase();
  if (mime.includes('pdf') || fileName.endsWith('.pdf')) return 'PDF';
  if (
    mime.includes('excel') ||
    mime.includes('spreadsheet') ||
    fileName.endsWith('.xls') ||
    fileName.endsWith('.xlsx') ||
    fileName.endsWith('.csv')
  ) return 'XLS';
  if (
    mime.startsWith('image/') ||
    /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(fileName)
  ) return 'IMG';
  if (
    mime.includes('text') ||
    mime.includes('word') ||
    fileName.endsWith('.txt') ||
    fileName.endsWith('.doc') ||
    fileName.endsWith('.docx') ||
    fileName.endsWith('.odt') ||
    fileName.endsWith('.rtf')
  ) return 'TXT';
  return 'FILE';
}

function boardPriorityRank(priority) {
  const p = String(priority || '').trim().toLowerCase();
  if (p === 'urgent') return 0;
  if (p === 'important') return 1;
  if (p === 'info') return 2;
  return 3;
}

function boardSortDate(item) {
  return String(item?.publishedAt || item?.updatedAt || item?.createdAt || '');
}

function formatDateTimeIt(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const normalized = raw.replace(' ', 'T');
  const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return raw;
  const [, y, mo, d, hh, mm, ss] = m;
  return `${d}-${mo}-${y}, ${hh}:${mm}:${ss || '00'}`;
}

function boardStatusLabel(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'in_review') return 'Da approvare';
  if (s === 'approved') return 'Pronta da pubblicare';
  if (s === 'published') return 'Pubblicata';
  if (s === 'rejected') return 'Rifiutata';
  if (s === 'draft') return 'Bozza';
  return status || '-';
}

function renderAttachments(attachments) {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!list.length) return '';
  return `
    <div class="board-attachments">
      ${list.map((a) => `
        <button
          type="button"
          class="btn btn-outline btn-sm board-attachment-link"
          data-action="download-attachment"
          data-url="${escapeAttr(a.url)}"
          data-filename="${escapeAttr(a.fileName || 'allegato')}"
        >
          <span class="board-attachment-type">${attachmentTypeSymbol(a)}</span>
          <span class="board-attachment-name">${escapeHtml(a.fileName || 'allegato')}</span>
        </button>
      `).join('')}
    </div>
  `;
}

export async function renderBoardView(container) {
  const user = getCurrentUser() || {};
  const canModerate = isModerationRole(user.role);
  const currentUserId = String(user?.userId || user?.id || '');
  const canCompose = /^(admin|organizer|director)$/.test(String(user.role || '').trim()) || Boolean(user?.doctorId || user?.doctor_id);
  container.innerHTML = `
    <div class="board-layout">
      <section id="board-feed-col"></section>
      <section id="board-side-col"></section>
    </div>
  `;
  const feedCol = container.querySelector('#board-feed-col');
  const sideCol = container.querySelector('#board-side-col');

  async function refreshFeed() {
    feedCol.innerHTML = '<div class="card"><div class="card-title">Bacheca reparto</div><p style="color:var(--text-secondary);">Caricamento feed...</p></div>';
    try {
      const data = await apiGetBoardFeed({ limit: 100 });
      const items = [...(data?.items || [])].sort((a, b) => {
        const byPriority = boardPriorityRank(a?.priority) - boardPriorityRank(b?.priority);
        if (byPriority !== 0) return byPriority;
        return boardSortDate(b).localeCompare(boardSortDate(a));
      });
      feedCol.innerHTML = `
        <div class="card">
          <div style="display:flex; justify-content:space-between; gap:1rem; align-items:center;">
            <div class="card-title">Bacheca reparto</div>
            <span class="badge">Non lette: ${data?.unreadCount || 0}</span>
          </div>
          <div style="margin-top:.6rem; display:flex; gap:.5rem; align-items:center;">
            <button id="board-refresh" class="btn btn-secondary btn-sm">Aggiorna</button>
          </div>
          ${items.length === 0 ? '<p style="margin-top:.8rem; color:var(--text-secondary);">Nessuna comunicazione disponibile.</p>' : ''}
          <div class="board-feed-list">
            ${items.map((it) => `
              <article class="board-item ${it.unread ? 'board-item--unread' : ''}" data-id="${it.id}">
                <header>
                  <strong>${escapeHtml(it.title)}</strong>
                  <div style="display:flex; gap:.35rem; align-items:center; flex-wrap:wrap; justify-content:flex-end;">
                    <span class="badge">${escapeHtml(it.priority)}</span>
                    <span class="badge board-status-badge board-status-badge--${escapeAttr(String(it.status || '').toLowerCase().replaceAll('_', '-'))}">${escapeHtml(boardStatusLabel(it.status))}</span>
                  </div>
                </header>
                <div class="board-meta">${escapeHtml(it.category)} · ${escapeHtml(formatDateTimeIt(it.publishedAt || it.createdAt || '-'))} · inviato da: ${escapeHtml(String(it.authorSigla || it.authorUsername || '-'))}</div>
                <p>${escapeHtml(it.body)}</p>
                ${renderAttachments(it.attachments)}
                <div class="board-actions">
                  <button class="btn btn-secondary btn-sm" data-action="read" ${it.acknowledged ? 'disabled' : ''}>Conferma lettura</button>
                  ${it.eventStartAt ? '<button class="btn btn-outline btn-sm" data-action="ics">Aggiungi al calendario</button>' : ''}
                  ${(currentUserId === String(it.authorUserId || '') || canModerate) ? '<button class="btn btn-outline btn-sm" data-action="read-status">Stato letture</button>' : ''}
                  ${(currentUserId === String(it.authorUserId || '') || canModerate) ? '<button class="btn btn-outline btn-sm" data-action="delete">Elimina</button>' : ''}
                </div>
              </article>
            `).join('')}
          </div>
        </div>
      `;
      feedCol.querySelector('#board-refresh')?.addEventListener('click', refreshFeed);
      feedCol.querySelectorAll('.board-item [data-action]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const action = btn.dataset.action;
          const article = btn.closest('.board-item');
          const postId = article?.dataset?.id;
          if (!postId) return;
          if (action === 'read') {
            const ok = window.confirm('Invio conferma di lettura?');
            if (!ok) return;
            try {
              await apiMarkBoardRead(postId, true, 'in_app');
              await refreshFeed();
            } catch (e) {
              window.alert(e?.message || 'Errore conferma lettura.');
            }
            return;
          }
          if (action === 'delete') {
            const ok = window.confirm('Eliminare definitivamente questa comunicazione?');
            if (!ok) return;
            try {
              await apiDeleteBoardPost(postId);
              await refreshFeed();
              if (canModerate) await renderBoardModerationView(sideCol.querySelector('#board-moderation-wrap'), { onChanged: refreshFeed });
            } catch (e) {
              window.alert(e?.message || 'Errore eliminazione comunicazione.');
            }
            return;
          }
          if (action === 'read-status') {
            try {
              const status = await apiGetBoardPostReadStatus(postId);
              openReadStatusModal(status);
            } catch (e) {
              window.alert(e?.message || 'Errore caricamento stato letture.');
            }
            return;
          }
          if (action === 'ics') {
            try {
              const result = await apiDownloadBoardPostCalendarIcs(postId);
              const url = URL.createObjectURL(result.blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = result.fileName || `board-event-${String(postId).slice(0, 8)}.ics`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (e) {
              window.alert(e?.message || 'Errore esportazione ICS evento.');
            }
            return;
          }
          if (action === 'download-attachment') {
            const relUrl = btn.dataset.url || '';
            const fallbackName = btn.dataset.filename || 'allegato';
            try {
              const result = await apiDownloadBoardAttachmentByUrl(relUrl);
              const url = URL.createObjectURL(result.blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = result.fileName || fallbackName;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (e) {
              window.alert(e?.message || 'Errore download allegato.');
            }
          }
        });
      });
    } catch (e) {
      feedCol.innerHTML = `<div class="card"><div class="card-title">Bacheca reparto</div><p style="color:var(--danger);">${e?.message || 'Errore caricamento feed.'}</p></div>`;
    }
  }

  if (canModerate) {
    sideCol.innerHTML = `
      <div id="board-moderation-wrap"></div>
      <div id="board-composer-wrap" style="margin-top:1rem;"></div>
    `;
    await renderBoardModerationView(sideCol.querySelector('#board-moderation-wrap'), {
      onChanged: refreshFeed,
    });
    renderBoardComposer(sideCol.querySelector('#board-composer-wrap'), {
      onCreated: refreshFeed,
    });
  } else if (canCompose) {
    sideCol.innerHTML = '<div id="board-composer-wrap"></div>';
    renderBoardComposer(sideCol.querySelector('#board-composer-wrap'), {
      onCreated: refreshFeed,
    });
  } else {
    sideCol.innerHTML = '';
  }

  await refreshFeed();
}
